import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage, defineMessages } from 'react-intl';

import { Switch } from '#src/atoms/inputs/Switch/index.tsx';
import { Bold } from '#src/atoms/typography/Bold/index.tsx';
import { SettingsSection } from '#src/components/settings/SettingsSection/index.tsx';
import { useQuery } from '#src/hooks/graphql/useQuery.ts';
import { LIFECYCLE, useLifecycle } from '#src/hooks/useLifecycle.ts';

import type {
  PrivacyQuery,
  PrivacyQueryVariables,
} from './__generated__/index.graphql.ts';

const messages = defineMessages({
  title: {
    defaultMessage: 'Hiding your response time',
  },
  description: {
    defaultMessage:
      "You can decide to hide your response time from other managers so they don't see how fast you typically answer trades.",
  },
  answersIn: {
    defaultMessage:
      'Other managers will see you typically answer in <bold>less than {hours, plural, one {# hour} other {# hours}}</bold>.',
  },
});

const PRIVACY_QUERY = gql`
  query PrivacyQuery {
    currentUser {
      slug
      hoursToAnswerTrades
    }
  }
` as TypedDocumentNode<PrivacyQuery, PrivacyQueryVariables>;

export const Privacy = () => {
  const { update, lifecycle } = useLifecycle();
  const hideActivity = lifecycle?.hideActivity;

  // we query the `hoursToAnswerTrades` lazily as it's a bit expensive
  const { data } = useQuery(PRIVACY_QUERY);

  return (
    <SettingsSection
      title={messages.title}
      description={messages.description}
      toggleButton={
        <Switch
          checked={hideActivity}
          onChange={() => {
            update(LIFECYCLE.hideActivity, !hideActivity);
          }}
        />
      }
    >
      {!!data?.currentUser?.hoursToAnswerTrades && (
        <span>
          <FormattedMessage
            {...messages.answersIn}
            values={{ hours: data.currentUser.hoursToAnswerTrades, bold: Bold }}
          />
        </span>
      )}
    </SettingsSection>
  );
};
